/**
 * Date and week pickers — design/09 §8: "date & week picker".
 *
 * Bespoke rather than `react-day-picker`, which is MIT but is not on the design/09
 * §10.1 register and would need G4 approval. More to the point, a retail planning
 * calendar is not a generic calendar:
 *
 *   · G6: "ISO-8601 week numbering (Monday start) is the platform standard for retail
 *     weeks, with the retail calendar (4-5-4 or equivalent) as configuration per
 *     country." A generic picker has no concept of a retail week and would need the
 *     week strip bolted on anyway.
 *   · G6: first day of week is locale configuration, not a constant.
 *   · Planners select WEEKS far more often than days — the week picker is the primary
 *     control and the date picker the secondary one, which is the reverse of a
 *     general-purpose library's assumption.
 *
 * Keyboard model: arrow keys move by day (or week), PageUp/PageDown by month,
 * Home/End to the start/end of the week, Enter to select, Escape to dismiss.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { Popover } from './overlays';
import { Button, IconButton } from './button';
import { FieldShell } from './fields';
import { FOCUS_RING_INSET } from './focus';
import { firstDayOfWeek, isoWeek, isoWeekLabel } from '../lib/format';

function startOfMonth(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}

function addMonths(d: Date, n: number): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1));
}

/** Grid of six weeks covering the month, aligned to the locale's first day. */
function monthGrid(month: Date, weekStart: 0 | 1): Date[][] {
  const first = startOfMonth(month);
  const offset = (first.getUTCDay() - weekStart + 7) % 7;
  const start = new Date(first);
  start.setUTCDate(first.getUTCDate() - offset);
  return Array.from({ length: 6 }, (_, w) => Array.from({ length: 7 }, (_, d) => {
    const day = new Date(start);
    day.setUTCDate(start.getUTCDate() + w * 7 + d);
    return day;
  }));
}

const isoDate = (d: Date): string => d.toISOString().slice(0, 10);

interface CalendarBodyProps {
  readonly month: Date;
  readonly onMonthChange: (month: Date) => void;
  readonly selectedIso: string | undefined;
  readonly onSelectDay: (day: Date) => void;
  /** Week mode highlights and selects a whole ISO week rather than a day. */
  readonly weekMode: boolean;
  readonly selectedWeek: string | undefined;
}

function CalendarBody({
  month, onMonthChange, selectedIso, onSelectDay, weekMode, selectedWeek,
}: CalendarBodyProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const weekStart = firstDayOfWeek(i18n.language);
  const grid = useMemo(() => monthGrid(month, weekStart), [month, weekStart]);

  const monthLabel = new Intl.DateTimeFormat(i18n.language, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(month);
  const weekdayFormat = new Intl.DateTimeFormat(i18n.language, { weekday: 'short', timeZone: 'UTC' });

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <IconButton
          variant="ghost"
          size="sm"
          icon={<ChevronLeft size={14} aria-hidden />}
          aria-label={t('picker.previousMonth')}
          onClick={() => onMonthChange(addMonths(month, -1))}
        />
        {/* Not Antonio: §4.2 caps the display face at two sizes, both ≥20px, and a
            calendar header is 12px. */}
        <span className="text-body font-semibold text-ink-primary">{monthLabel}</span>
        <IconButton
          variant="ghost"
          size="sm"
          icon={<ChevronRight size={14} aria-hidden />}
          aria-label={t('picker.nextMonth')}
          onClick={() => onMonthChange(addMonths(month, 1))}
        />
      </div>
      <table className="border-collapse" role="grid">
        <thead>
          <tr>
            {/* The week-number column is always present. In a planning tool the week
                number is the primary coordinate, not an optional extra. */}
            {/* `ink-secondary`, matching the weekday headers beside it — ADR 0092. */}
            <th scope="col" className="text-micro text-ink-secondary px-1 uppercase tracking-[0.04em]">
              {t('picker.weekAbbrev')}
            </th>
            {grid[0]?.map((day) => (
              <th key={isoDate(day)} scope="col" className="text-micro text-ink-secondary w-8 px-1 uppercase tracking-[0.04em]">
                {weekdayFormat.format(day)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.map((week) => {
            const anchor = week[0];
            if (!anchor) return null;
            const label = isoWeekLabel(anchor);
            const weekSelected = weekMode && selectedWeek === label;
            return (
              <tr key={label}>
                {/* The week number is §12.7 body text and, in a planning tool, the primary
                    coordinate. `ink-secondary` — ADR 0092. */}
                <th scope="row" className="text-numeric text-ink-secondary font-numeric px-1 text-end tabular-nums">
                  {weekMode
                    ? (
                      <button
                        type="button"
                        onClick={() => onSelectDay(anchor)}
                        aria-pressed={weekSelected}
                        className={`rounded-chip px-1 ${weekSelected ? 'bg-ink-primary text-surface-raised' : ''} ${FOCUS_RING_INSET}`}
                      >
                        {isoWeek(anchor).week}
                      </button>
                    )
                    : isoWeek(anchor).week}
                </th>
                {week.map((day) => {
                  const outside = day.getUTCMonth() !== month.getUTCMonth();
                  const selected = !weekMode && selectedIso === isoDate(day);
                  return (
                    <td key={isoDate(day)} className="p-0">
                      <button
                        type="button"
                        onClick={() => onSelectDay(day)}
                        aria-selected={selected || weekSelected}
                        className={[
                          'text-numeric font-numeric size-8 rounded-chip tabular-nums',
                          // A day outside the displayed month is still a real, clickable date
                          // with real text on it, so §12.7 applies to it in full. `ink-secondary`
                          // keeps it visibly subordinate to `ink-primary` AND clears 4.5:1;
                          // `ink-muted` carried the distinction at 2.17:1. ADR 0092.
                          outside ? 'text-ink-secondary' : 'text-ink-primary',
                          selected || weekSelected ? 'bg-ink-primary text-surface-raised' : 'hover:bg-surface-sunken',
                          FOCUS_RING_INSET,
                        ].join(' ')}
                      >
                        {day.getUTCDate()}
                      </button>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export interface DatePickerProps {
  readonly labelKey: string;
  readonly helpKey?: string;
  readonly errorKey?: string;
  /** ISO-8601 date, `YYYY-MM-DD`. Stored UTC, displayed in the user's timezone (G6). */
  readonly value?: string;
  readonly onValueChange?: (iso: string) => void;
}

export function DatePicker({ labelKey, helpKey, errorKey, value, onValueChange }: DatePickerProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [month, setMonth] = useState(() => startOfMonth(value ? new Date(value) : new Date()));

  const display = value
    ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(value))
    : t('picker.selectDate');

  return (
    <FieldShell
      labelKey={labelKey}
      {...(helpKey === undefined ? {} : { helpKey })}
      {...(errorKey === undefined ? {} : { errorKey })}
    >
      {() => (
        <Popover
          triggerLabelKey="picker.selectDate"
          trigger={
            /* Start-aligned with the chevron at the far end, like every other field in a
               form. Centred, a date trigger stretched to its column put "24 Sept 2026" in
               the middle of a 365px pill, aligned with nothing around it (audit v4 G-20). */
            <Button
              variant="secondary"
              align="start"
              icon={<ChevronDown size={14} aria-hidden />}
              aria-label={t(labelKey)}
            >
              <span className="flex items-center gap-2">
                <Calendar size={14} aria-hidden />
                {/* A chosen date is DATA, formatted by `Intl` for the locale, not a catalogue
                    string — and `<time>` is HTML's own way to say so, with the machine value
                    beside it. The pseudo-locale test reads it that way (it reported "24 Sept
                    2026" as hard-coded copy). The prompt shown before a choice stays copy. */}
                {value ? <time dateTime={value}>{display}</time> : display}
              </span>
            </Button>
          }
        >
          <CalendarBody
            month={month}
            onMonthChange={setMonth}
            selectedIso={value}
            selectedWeek={undefined}
            weekMode={false}
            onSelectDay={(day) => onValueChange?.(isoDate(day))}
          />
        </Popover>
      )}
    </FieldShell>
  );
}

export interface WeekPickerProps {
  readonly labelKey: string;
  readonly helpKey?: string;
  readonly errorKey?: string;
  /** ISO week identifier, `YYYY-Www`. The platform standard for retail weeks (G6). */
  readonly value?: string;
  readonly onValueChange?: (isoWeekLabel: string) => void;
}

export function WeekPicker({ labelKey, helpKey, errorKey, value, onValueChange }: WeekPickerProps): React.JSX.Element {
  const { t } = useTranslation();
  const [month, setMonth] = useState(() => startOfMonth(new Date()));

  return (
    <FieldShell
      labelKey={labelKey}
      {...(helpKey === undefined ? {} : { helpKey })}
      {...(errorKey === undefined ? {} : { errorKey })}
    >
      {() => (
        <Popover
          triggerLabelKey="picker.selectWeek"
          trigger={
            <Button
              variant="secondary"
              align="start"
              icon={<ChevronDown size={14} aria-hidden />}
              aria-label={t(labelKey)}
            >
              {/* One group, so `justify-between` puts the chevron at the far end and not the
                  week in the middle. */}
              <span className="flex items-center gap-2">
                <Calendar size={14} aria-hidden className="shrink-0" />
                {/* The week identifier is an identifier, not prose — it is not localised,
                    which is exactly why it is stable across the three locales. */}
                <span className="font-numeric text-numeric tabular-nums">{value ?? t('picker.selectWeek')}</span>
              </span>
            </Button>
          }
        >
          <CalendarBody
            month={month}
            onMonthChange={setMonth}
            selectedIso={undefined}
            selectedWeek={value}
            weekMode
            onSelectDay={(day) => onValueChange?.(isoWeekLabel(day))}
          />
        </Popover>
      )}
    </FieldShell>
  );
}
